import { Link } from 'react-router-dom';
import { FileText, AlertCircle, Scale, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

export const Terms = () => {
  const lastUpdated = "December 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Website Terms of Use - Crescent Electrical Ltd"
        description="Terms and conditions of use for the Crescent Electrical Ltd website."
        canonical="https://crescentelectrical.co.uk/terms"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2563EB] to-blue-700 text-white py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-10 w-10" />
            <h1 className="text-4xl sm:text-5xl font-bold">Website Terms of Use</h1>
          </div>
          <p className="text-xl text-blue-100">
            Terms and conditions for using the Crescent Electrical Ltd website
          </p>
          <p className="text-sm text-blue-200 mt-4">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
            <div className="prose prose-blue max-w-none">
              
              {/* Introduction */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. About These Terms</h2>
                <p className="text-gray-700 mb-3">
                  These terms of use govern your use of the Crescent Electrical Ltd website at <strong>crescentelectrical.co.uk</strong> (the "Website").
                </p>
                <p className="text-gray-700 mb-3">
                  By accessing and using this Website, you accept and agree to be bound by these terms. If you do not agree with any part of these terms, please do not use our Website.
                </p>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="space-y-2 text-sm">
                      <p><strong>Website operated by:</strong> Crescent Electrical Ltd</p>
                      <p><strong>Company number:</strong> 10335734</p>
                      <p><strong>Registered office:</strong> 67a Station Road, London, England, E4 7BU</p>
                      <p><strong>Registered in:</strong> England and Wales</p>
                      <p><strong>Email:</strong> <a href="mailto:info@electriccertificate.co.uk" className="text-[#2563EB] hover:underline">info@electriccertificate.co.uk</a></p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Use of Website */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Use of This Website</h2>
                <p className="text-gray-700 mb-3">
                  This Website is provided for information purposes to help you learn about our electrical services and request quotations.
                </p>
                <p className="text-gray-700 mb-3">You agree to use this Website only for lawful purposes and in a manner that does not:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Infringe the rights of others</li>
                  <li>Restrict or inhibit anyone else's use of the Website</li>
                  <li>Transmit harmful code, viruses, or malware</li>
                  <li>Attempt to gain unauthorised access to our systems</li>
                  <li>Use automated systems to scrape or extract content</li>
                  <li>Impersonate any person or misrepresent your affiliation</li>
                </ul>
              </div>

              {/* Information Accuracy */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="h-6 w-6 text-[#2563EB]" />
                  3. Information & Accuracy
                </h2>
                <p className="text-gray-700 mb-3">
                  We make reasonable efforts to ensure that information on this Website is accurate and up to date. However:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Information is provided for general guidance only and should not be relied upon as professional advice</li>
                  <li>We do not guarantee that the Website will be error-free or uninterrupted</li>
                  <li>Technical specifications, regulations, and standards may change</li>
                  <li>We reserve the right to update or modify content at any time without notice</li>
                </ul>
                <Card className="bg-amber-50 border-amber-200 mt-4">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-700">
                      <strong>Important:</strong> For electrical work advice specific to your property, always consult a qualified electrician or request a formal site survey.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Prices & Quotes */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <DollarSign className="h-6 w-6 text-[#2563EB]" />
                  4. Prices, Estimates & Quotations
                </h2>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.1 Online Pricing Information</h3>
                <p className="text-gray-700 mb-3">
                  Prices displayed on this Website (including rewire calculator estimates) are provided as guidance only and:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Are based on standard specifications, empty properties, and average access conditions</li>
                  <li>Assume white electrical accessories unless otherwise stated</li>
                  <li>Do not automatically constitute a binding quotation or offer to contract</li>
                  <li>May vary depending on actual property conditions, access, materials, and scope of work</li>
                  <li>Are subject to change without notice</li>
                  <li>Exclude VAT unless stated otherwise</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.2 Rewire Calculator</h3>
                <p className="text-gray-700 mb-3">
                  Our online rewire calculator provides <strong>indicative estimates</strong> only. These are not guaranteed prices. Actual costs will be confirmed after:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Property survey and assessment</li>
                  <li>Confirmation of scope of work</li>
                  <li>Agreement on specification and materials</li>
                  <li>Issue of a formal written quotation</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.3 Formal Quotations</h3>
                <p className="text-gray-700 mb-3">
                  A binding contract is only formed when:
                </p>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>We issue you a formal written quotation</li>
                  <li>You accept that quotation in writing (email acceptable)</li>
                  <li>Any required deposit is paid</li>
                </ol>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.4 "From" Pricing</h3>
                <p className="text-gray-700">
                  Where prices are stated as "from £X", this indicates a starting price for the most basic specification under ideal conditions. Your actual price may be higher depending on your requirements.
                </p>
              </div>

              {/* Intellectual Property */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
                <p className="text-gray-700 mb-3">
                  All content on this Website (including text, images, logos, design, and code) is owned by or licensed to Crescent Electrical Ltd and is protected by UK and international intellectual property laws.
                </p>
                <p className="text-gray-700 mb-3">
                  You may view and print content for personal, non-commercial use only. You may not:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Reproduce content for commercial purposes without permission</li>
                  <li>Modify, distribute, or republish Website content</li>
                  <li>Remove copyright or trademark notices</li>
                  <li>Use our branding, logos, or images on other websites</li>
                </ul>
              </div>

              {/* External Links */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Links to External Websites</h2>
                <p className="text-gray-700 mb-3">
                  This Website may contain links to third-party websites (such as regulatory bodies, manufacturers, or service providers). These links are provided for your convenience only.
                </p>
                <p className="text-gray-700">
                  We do not control or endorse external websites and are not responsible for their content, privacy practices, or availability. Use of external websites is at your own risk.
                </p>
              </div>

              {/* Limitation of Liability */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Scale className="h-6 w-6 text-[#2563EB]" />
                  7. Limitation of Liability
                </h2>
                <p className="text-gray-700 mb-3">
                  To the fullest extent permitted by law:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>This Website is provided "as is" without warranties of any kind, express or implied</li>
                  <li>We do not warrant that the Website will be uninterrupted, error-free, or free from viruses</li>
                  <li>We are not liable for any loss or damage arising from your use of (or inability to use) this Website</li>
                  <li>We are not liable for inaccuracies or omissions in Website content</li>
                </ul>

                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Important Legal Notice:</strong>
                    </p>
                    <p className="text-sm text-gray-700">
                      Nothing in these terms excludes or limits our liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded or limited under UK law.
                    </p>
                    <p className="text-sm text-gray-700 mt-2">
                      Where we provide electrical installation services under a contract with you, your statutory consumer rights remain unaffected. These terms relate only to your use of our Website.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Availability */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Website Availability</h2>
                <p className="text-gray-700">
                  We aim to keep the Website available 24/7, but we do not guarantee continuous access. The Website may be unavailable due to maintenance, updates, technical issues, or circumstances beyond our control. We may suspend, withdraw, or modify the Website at any time without liability.
                </p>
              </div>

              {/* User Content */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Content You Submit</h2>
                <p className="text-gray-700 mb-3">
                  When you submit enquiries, photographs, floor plans, or other information through our Website:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                  <li>You warrant that you have the right to submit such content</li>
                  <li>You grant us permission to use the content to provide our services and prepare quotations</li>
                  <li>You must not submit content that is unlawful, defamatory, obscene, or infringes third-party rights</li>
                </ul>
                <p className="text-gray-700">
                  See our <Link to="/privacy" className="text-[#2563EB] hover:underline">Privacy Policy</Link> for information on how we handle personal data you provide.
                </p>
              </div>

              {/* Changes to Terms */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to These Terms</h2>
                <p className="text-gray-700">
                  We may update these terms from time to time. Changes will be posted on this page with a revised "last updated" date. Your continued use of the Website after changes are posted constitutes acceptance of the updated terms.
                </p>
              </div>

              {/* Governing Law */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law & Jurisdiction</h2>
                <p className="text-gray-700 mb-3">
                  These terms are governed by the laws of England and Wales.
                </p>
                <p className="text-gray-700">
                  Any disputes arising from your use of this Website shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                </p>
              </div>

              {/* Severability */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Severability</h2>
                <p className="text-gray-700">
                  If any provision of these terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
                </p>
              </div>

              {/* Contact */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
                <p className="text-gray-700 mb-3">
                  If you have any questions about these terms, please contact us:
                </p>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="space-y-2 text-sm">
                      <p><strong>Crescent Electrical Ltd</strong></p>
                      <p>67a Station Road, London, E4 7BU</p>
                      <p><strong>Email:</strong> <a href="mailto:info@electriccertificate.co.uk" className="text-[#2563EB] hover:underline">info@electriccertificate.co.uk</a></p>
                      <p><strong>Phone:</strong> <a href="tel:07711233685" className="text-[#2563EB] hover:underline">07711 233685</a></p>
                    </div>
                  </CardContent>
                </Card>
              </div>

            </div>
          </div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/privacy">
              <Card className="border-gray-200 hover:border-[#2563EB] hover:shadow-lg transition-all h-full">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Privacy Policy</h3>
                  <p className="text-sm text-gray-600">How we handle your personal information</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/cookies">
              <Card className="border-gray-200 hover:border-[#2563EB] hover:shadow-lg transition-all h-full">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Cookie Policy</h3>
                  <p className="text-sm text-gray-600">Information about cookies we use</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
